<template>
  <Modal
    v-model:visible="isVisible"
    class="document-email-modal"
    :header="t('documents.emailDocuments')"
    :dismissable-mask="!isAwaitingEmailSent"
    @keypress.enter="onSend"
  >
    <div v-if="hasConfirmedSelections">
      <InputWrapper
        v-slot="{ ariaProps, inputClasses, inputId, invalid }"
        input-id="email-document-modal-emails"
        :label="t('shipments.emailsCommaSeparated')"
        :errors="v$.email.$errors"
      >
        <InputText
          :id="inputId"
          v-model="emails.email"
          v-bind="ariaProps"
          :class="inputClasses"
          :invalid="invalid"
          :placeholder="t('shipments.emailsCommaSeparated')"
        />
      </InputWrapper>
    </div>

    <div v-else>
      <p class="section-header">
        {{ emailInstructionMessage }}
      </p>
      <DocumentEmailCheckboxContainer
        v-if="documents.data"
        v-model="selectedDocuments"
        v-model:email-bol-selected="emailBolSelected"
        :documents="combinedDocuments"
        :can-email-bol="canEmailBol"
      />
    </div>

    <template #footer>
      <RygenButton
        mode="tertiary"
        :label="t('common.cancel')"
        @click="onCancel"
      />

      <RygenButton
        v-if="hasConfirmedSelections"
        :label="t('common.send')"
        :loading="isAwaitingEmailSent"
        @click="onSend"
      />
      <RygenButton
        v-else
        :label="t('common.next')"
        :disabled="!hasSelectedOptions"
        @click="onNextClicked"
      />
    </template>
  </Modal>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import {
  matchesEmail,
  required,
  useUid,
  useValidations,
} from '@rygen/rygen-ui/composables';

import { useDocuments, useRelatedDocuments } from '@/composables';
import { Shipment, ShipmentStatus } from '@/model';
import { useParentEntityStore } from '@/stores/ParentEntity';

import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import InputWrapper from '@rygen/rygen-ui/components/form/InputWrapper.vue';
import Modal from '@rygen/rygen-ui/components/ui/Modal.vue';
import InputText from 'primevue/inputtext';

import DocumentEmailCheckboxContainer from './DocumentEmailCheckboxContainer.vue';

const isVisible = defineModel('visible', {
  type: Boolean,
  default: () => false,
});

const emails = ref({ email: '' });

const hasConfirmedSelections = ref(false);
const emailBolSelected = ref(false);
const isAwaitingEmailSent = ref(false);
const selectedDocuments = ref([]);

const { documents, emailDocuments } = useDocuments();
const { parentEntity } = useParentEntityStore();
const { relatedDocuments } = useRelatedDocuments();
const { t } = useI18n();

const rules = {
  email: {
    required: required(t('common.email')),
    matches: matchesEmail(),
  },
};

const { uid } = useUid('document-email-modal');
const { v$, validate } = useValidations(emails, rules, uid);

const hasSelectedOptions = computed(
  () => selectedDocuments.value.length > 0 || emailBolSelected.value,
);

const emailInstructionMessage = computed(() => {
  if (
    (documents.value.data && documents.value.data.length > 0) ||
    (relatedDocuments.value.data && relatedDocuments.value.data.length > 0) ||
    canEmailBol.value
  ) {
    return t('documents.emailDocumentInstructions');
  }
  return t('common.noParam', { param: t('documents.documents', 2) });
});

const emailBolByStatus = [
  ShipmentStatus.CARRIER_ASSIGNED,
  ShipmentStatus.TENDERED,
  ShipmentStatus.TENDER_REJECTED,
  ShipmentStatus.BOOKED,
  ShipmentStatus.IN_TRANSIT,
  ShipmentStatus.DELIVERED,
];

const canEmailBol = computed(() => {
  if (parentEntity.value instanceof Shipment && parentEntity.value.status) {
    return emailBolByStatus.includes(parentEntity.value.status);
  }
  return false;
});

const combinedDocuments = computed(() => {
  const docs = [];
  docs.push(
    ...(documents.value.data || []),
    ...(relatedDocuments.value.data || []),
  );
  return docs;
});

const clearEmailInfo = (): void => {
  selectedDocuments.value = [];
  emails.value.email = '';
  hasConfirmedSelections.value = false;
  emailBolSelected.value = false;
};

watch(
  isVisible,
  (newVal: boolean) => {
    if (!newVal) {
      clearEmailInfo();
    }
  },
  { immediate: true },
);

const onNextClicked = (): void => {
  hasConfirmedSelections.value = true;
};

const onSend = async () => {
  if (await validate()) {
    isAwaitingEmailSent.value = true;
    emailDocuments(
      selectedDocuments.value,
      emailBolSelected.value,
      emails.value.email,
    );
    isAwaitingEmailSent.value = false;
    isVisible.value = false;
  }
};

const onCancel = () => {
  isVisible.value = false;
};
</script>

<style>
.document-email-modal.p-dialog.p-component {
  .p-dialog-header {
    padding-bottom: 0;
  }

  .p-dialog-content {
    .modal-content {
      &.scrollable {
        /* prevent horizontal scrolling but still allow vertical scrolling */
        overflow: hidden auto;
      }

      .checkbox-container {
        display: flex;
        gap: var(--sp-1);
        max-width: calc(100% - var(--sp-6));
        padding-top: var(--sp-1);
        padding-bottom: var(--sp-1);

        .p-checkbox {
          flex: 0 0 auto;
        }

        .document-details {
          flex-direction: column;
          align-items: left;
          max-width: 100%;

          .document-name {
            display: block;
            white-space: nowrap;
            overflow: hidden;
            text-overflow: ellipsis;
            padding: var(--sp-1);
          }

          .document-type-chips {
            gap: var(--sp-1);
          }
        }
      }
    }
  }
}
</style>
